import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { EnhancedFAQs } from "@/components/seo/EnhancedFAQs";
import { AuthorBio } from "@/components/content/AuthorBio";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { RelatedResources } from "@/components/seo/RelatedResources";
import { BreadcrumbNavigation } from "@/components/seo/BreadcrumbNavigation";

const WEST_STATES = [
  { name: 'Alaska', abbr: 'AK', url: 'alaska' },
  { name: 'Arizona', abbr: 'AZ', url: 'arizona' },
  { name: 'California', abbr: 'CA', url: 'california' },
  { name: 'Colorado', abbr: 'CO', url: 'colorado' },
  { name: 'Hawaii', abbr: 'HI', url: 'hawaii' },
  { name: 'Idaho', abbr: 'ID', url: 'idaho' },
  { name: 'Montana', abbr: 'MT', url: 'montana' },
  { name: 'Nevada', abbr: 'NV', url: 'nevada' },
  { name: 'New Mexico', abbr: 'NM', url: 'new-mexico' },
  { name: 'Oregon', abbr: 'OR', url: 'oregon' },
  { name: 'Utah', abbr: 'UT', url: 'utah' },
  { name: 'Washington', abbr: 'WA', url: 'washington' },
  { name: 'Wyoming', abbr: 'WY', url: 'wyoming' }
];

const WEST_FAQS = [
  {
    question: "How do Western PEOs handle California's complex employment laws?",
    answer: "Western PEOs, especially those with California experience, maintain dedicated compliance teams for the state's unique requirements including PAGA, detailed meal and rest break rules, comprehensive paid sick leave, and extensive anti-discrimination protections. They invest heavily in staying current with California's frequently changing regulations."
  },
  {
    question: "Do Western PEOs understand the tech industry culture?",
    answer: "Yes, many Western PEOs specialize in tech companies and understand the industry's unique needs including equity compensation, flexible work arrangements, competitive benefits, and the importance of company culture in talent retention. They're experienced with rapid scaling and international hiring common in tech."
  },
  {
    question: "How do PEOs help with the West's remote workforce management?",
    answer: "Western PEOs are pioneers in remote workforce solutions, offering robust technology platforms, experience with distributed teams, multi-state compliance for remote workers, and innovative benefits that support work-life balance valued by Western employees."
  },
  {
    question: "What makes Western PEOs different for innovation-focused companies?",
    answer: "Western PEOs understand the innovation economy and provide flexible HR solutions that support rapid growth, changing business models, and the need to attract top talent in competitive markets. They often offer more progressive benefits and workplace policies."
  }
];

const RELATED_RESOURCES = [
  {
    title: "California PEO Services",
    description: "Complete guide to PEO services in California",
    url: "/states/california",
    type: "state-guide" as const
  },
  {
    title: "Washington PEO Providers",
    description: "Find the best PEO companies in Washington",
    url: "/states/washington",
    type: "state-guide" as const
  },
  {
    title: "Technology Industry PEOs",
    description: "Specialized PEO services for tech companies",
    url: "/industries/technology",
    type: "industry-guide" as const
  },
  {
    title: "Professional Services PEOs",
    description: "PEO solutions for professional service firms",
    url: "/industries/professional-services",
    type: "industry-guide" as const
  }
];

export const WestRegion = () => {
  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title="West PEO Services: Best Professional Employer Organizations in the West"
        description="Find top PEO companies serving the Western region. Expert comparison of Professional Employer Organizations in CA, WA, OR, CO, AZ, and all Western states. Get matched with the perfect PEO for your business."
        keywords="West PEO, California PEO, Washington PEO, Oregon PEO, Colorado PEO, Arizona PEO, Western Professional Employer Organization"
        canonical="https://thepeoapp.com/regions/west"
      />
      
      <LocalBusinessSchema
        businessName="The PEO App - West Region"
        serviceType="Professional Employer Organization Services"
        areaServed="Western United States"
        description="Professional Employer Organization matching services for businesses in the Western region"
        url="https://thepeoapp.com/regions/west"
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation 
            items={[
              { label: "Home", href: "/" },
              { label: "States", href: "/states" },
              { label: "West Region", href: "/regions/west" }
            ]}
          />
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              West PEO Services: Professional Employer Organizations
            </h1>
            
            <div className="prose prose-lg max-w-none mb-12 text-muted-foreground">
              <p className="text-xl leading-relaxed">
                The Western United States leads the nation in innovation, from Silicon Valley tech giants to emerging startups across the region. Our vetted Professional Employer Organizations understand the unique challenges of Western businesses and provide cutting-edge HR solutions.
              </p>
            </div>

            <div className="bg-card p-8 rounded-xl shadow-lg mb-12">
              <h2 className="text-2xl font-bold mb-6">Why Choose a Western PEO?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Innovation Focus</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Cutting-edge HR technology platforms</li>
                    <li>• Experience with equity compensation</li>
                    <li>• Progressive workplace policies</li>
                    <li>• Support for rapid scaling companies</li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Regional Expertise</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Deep knowledge of California employment law</li>
                    <li>• Remote workforce management expertise</li>
                    <li>• Understanding of competitive talent markets</li>
                    <li>• Experience with diverse geography challenges</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-6">Western States We Serve</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {WEST_STATES.map((state) => (
                  <Link
                    key={state.abbr}
                    to={`/states/${state.url}`}
                    className="bg-background p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-2 border"
                  >
                    <span className="text-primary font-semibold">{state.abbr}</span>
                    <span className="text-foreground">{state.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-center mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready to Find Your Western PEO?</h2>
              <p className="text-muted-foreground mb-6">
                Get matched with PEO providers who understand the Western innovation economy
              </p>
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free PEO Quotes
                </Button>
              </Link>
            </div>

            <RelatedResources 
              title="Related Western Resources"
              resources={RELATED_RESOURCES}
            />

            <div className="mt-16">
              <EnhancedFAQs 
                title="Western PEO FAQ"
                faqs={WEST_FAQS}
                showSchema={true}
              />
            </div>

            <div className="mt-16">
              <AuthorBio showFullBio={false} />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};